import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Tag, ShoppingCart, Calendar, MapPin, Loader2 } from "lucide-react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { useAuth } from "@/hooks/useAuth";
import { getActiveResaleListings } from "@/lib/resale-v2.functions";
import { formatPrice, formatDate } from "@/lib/mock-data";
import { supabase } from "@/integrations/supabase/client";
import { getStripe, stripeEnvironment } from "@/lib/stripe";
import { getCheckoutFunctionName, logCheckoutProvider } from "@/lib/payment-provider";

export const Route = createFileRoute("/resale")({
  component: ResaleMarketplace,
  head: () => ({
    meta: [
      { title: "Reventa Oficial — ZYNTRA" },
      { name: "description", content: "Comprá entradas de reventa verificadas con precio controlado." },
    ],
  }),
});

function ResaleMarketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  // Stable idempotency keys per listing — survives accidental double-clicks
  const idemKeysRef = useRef<Record<string, string>>({});

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const result = await getActiveResaleListings();
      setListings(result.listings);
    } catch { setListings([]); }
    setLoading(false);
  }

  async function handleBuy(listingId: string) {
    if (!user) {
      navigate({
        to: "/login",
        search: { redirect: `${window.location.pathname}${window.location.search}` } as any,
      });
      return;
    }
    setBuyingId(listingId);
    setMessage({ text: "", type: "" });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      // Idempotency-Key: stable per listing+user attempt; survives accidental double-clicks
      // Idempotency-Key: stable per listing for this user, regenerated only after error
      if (!idemKeysRef.current[listingId]) {
        const raw =
          globalThis.crypto?.randomUUID?.() ?? `${listingId}-${Date.now()}`;
        idemKeysRef.current[listingId] = raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
      }
      const idemKey = idemKeysRef.current[listingId];
      const headers: Record<string, string> = { "Idempotency-Key": idemKey };
      if (token) headers.Authorization = `Bearer ${token}`;
      const provider = logCheckoutProvider("resale");
      const fnName = getCheckoutFunctionName("resale", provider);
      const { data, error } = await supabase.functions.invoke(fnName, {
        body: { listingId, environment: stripeEnvironment },
        headers,
      });
      // Try to surface the real error body from the edge function
      let realError: string | null = null;
      if (error) {
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            realError = body?.error || null;
          }
        } catch { /* ignore */ }
      }
      if (provider === "onvopay") {
        if (error || !data?.url) {
          throw new Error(realError || data?.error || error?.message || "Error al iniciar pago");
        }
        window.location.href = data.url;
        return;
      }
      if (error || !data?.clientSecret) {
        throw new Error(realError || data?.error || error?.message || "Error al iniciar pago");
      }
      setClientSecret(data.clientSecret);
    } catch (err: any) {
      setMessage({ text: err.message || "Error", type: "error" });
      setBuyingId(null);
    }
  }

  if (clientSecret) {
    return (
      <div className="mx-auto max-w-2xl px-4 pb-20 pt-6 sm:px-6">
        <button onClick={() => { if (buyingId) delete idemKeysRef.current[buyingId]; setClientSecret(null); setBuyingId(null); load(); }} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Cancelar
        </button>
        <div className="rounded-2xl border border-border bg-card p-2 overflow-hidden">
          <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret: async () => clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 pb-20 pt-6 sm:px-6">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Volver
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Tag className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-black text-foreground sm:text-3xl">Reventa Oficial</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-2">
        Entradas verificadas con precio controlado. El pago es seguro y la transferencia es instantánea.
      </p>
      <p className="text-xs text-muted-foreground/80 mb-8">
        Los precios mostrados son el total que pagás por la entrada.
      </p>

      {message.text && (
        <div className={`mb-6 rounded-xl px-4 py-3 text-sm font-medium ${
          message.type === "success" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
        }`}>{message.text}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : listings.length === 0 ? (
        <div className="mt-16 text-center">
          <Tag className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <p className="mt-4 text-muted-foreground">No hay entradas en reventa.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map((listing) => {
            const ev = listing.events;
            return (
              <div key={listing.id} className="rounded-2xl border border-border bg-card p-5 transition-all card-hover">
                <div className="flex gap-4">
                  {ev?.image_url && (
                    <img src={ev.image_url} alt={ev.title} className="h-20 w-32 rounded-xl object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-card-foreground truncate">{ev?.title || "Evento"}</h3>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {ev ? formatDate(ev.date) : ""}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {ev ? `${ev.location}, ${ev.city}` : ""}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        {listing.tickets?.ticket_label && (
                          <span className="text-xs text-muted-foreground">{listing.tickets.ticket_label}</span>
                        )}
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-black text-primary">{formatPrice(listing.listing_price)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBuy(listing.id)}
                        disabled={buyingId === listing.id}
                        className="flex items-center gap-1.5 rounded-xl gradient-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-all hover:opacity-90 glow-primary disabled:opacity-50"
                      >
                        <ShoppingCart className="h-3.5 w-3.5" />
                        {buyingId === listing.id ? "Iniciando..." : "Comprar"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
